// Card primitive for canonical document listings.

import React from 'react';
import { Link } from 'react-router';
import { ASSETS } from '@airhead-games/shared/configs';
import type { Document } from '@airhead-games/shared/content';
import { formatUtcDateForUserLocale } from '@airhead-games/shared/utilities';
import DocumentGradientCard from '@/content/rendering/documentListings/DocumentGradientCard';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/utilities/cn';
import type { PlayerColorFamily } from '@/styles/playerColors';

interface DocumentListingCardProps {
  document: Document;
  compact?: boolean;
  featured?: boolean;
  variant?: 'default' | 'report-month';
  accentFamily?: PlayerColorFamily;
}

const getReportMonthPartsFromMeta = (reportMonth?: string) => {
  const match = reportMonth?.match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return { month: 'Unknown', year: '' };
  }

  const year = match[1];
  const monthIndex = Number.parseInt(match[2], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    return { month: 'Unknown', year };
  }

  const month = new Date(Date.UTC(2000, monthIndex, 1)).toLocaleString(
    'en-US',
    { month: 'long', timeZone: 'UTC' }
  );
  return { month, year };
};

const DocumentListingCard: React.FC<DocumentListingCardProps> = ({
  document,
  compact,
  featured,
  variant = 'default',
  accentFamily,
}) => {
  const imageAsset = document.hero?.imageAsset ?? document.seo.imageAsset;
  const publishedAt = formatUtcDateForUserLocale(document.meta?.publishedAt, {
    month: 'short',
  });
  const reportMonthParts = getReportMonthPartsFromMeta(
    document.meta?.reportMonth
  );
  const { month: reportMonth, year: reportYear } = reportMonthParts;

  if (variant === 'report-month') {
    return (
      <Link
        to={document.route}
        aria-label={document.seo.title}
        className="group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <DocumentGradientCard
          title={reportMonth}
          eyebrow={reportYear}
          accentFamily={accentFamily ?? 'red'}
          className={cn(
            'min-h-24 sm:min-h-28 lg:min-h-32',
            featured && 'lg:min-h-[20rem] xl:min-h-[21rem]'
          )}
          titleClassName={cn(
            'text-2xl sm:text-3xl lg:text-4xl',
            featured && 'lg:text-5xl xl:text-6xl'
          )}
        />
      </Link>
    );
  }

  return (
    <Link
      to={document.route}
      aria-label={document.seo.title}
      className="group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <Card
        className={cn(
          'h-full overflow-hidden pt-0 ring-border transition-all hover:bg-card hover:ring-primary hover:shadow-sm',
          featured &&
            'sm:gap-0 sm:py-0 lg:grid lg:h-[20rem] lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:items-stretch xl:h-[21rem]'
        )}
      >
        {imageAsset && !compact && (
          <div
            className={cn(
              'bg-muted',
              featured && 'lg:h-full lg:min-h-0 lg:self-stretch'
            )}
          >
            <img
              src={ASSETS[imageAsset]}
              alt={document.hero?.imageAlt ?? document.seo.title}
              loading="lazy"
              className={cn(
                'aspect-[16/9] w-full object-cover',
                featured && 'lg:aspect-auto lg:block lg:size-full'
              )}
            />
          </div>
        )}
        <CardHeader
          className={cn(
            'gap-2',
            featured &&
              'sm:flex sm:h-full sm:flex-col sm:justify-center sm:gap-2 sm:p-6 sm:text-left lg:p-7 xl:p-8'
          )}
        >
          {publishedAt && !compact && (
            <span
              className={cn(
                'text-xs leading-5 text-muted-foreground',
                featured && 'sm:text-sm sm:leading-5'
              )}
            >
              {publishedAt}
            </span>
          )}
          <CardTitle
            className={cn(
              'text-lg leading-tight tracking-tight',
              featured && 'sm:text-3xl sm:leading-tight xl:text-4xl'
            )}
          >
            {document.seo.title}
          </CardTitle>
          {!compact && (
            <CardDescription
              className={cn(
                'line-clamp-3 text-sm leading-6',
                featured && 'sm:text-base sm:leading-6 xl:text-lg xl:leading-7'
              )}
            >
              {document.seo.description}
            </CardDescription>
          )}
        </CardHeader>
      </Card>
    </Link>
  );
};

export default DocumentListingCard;
